/* eslint no-console: 0, no-native-reassign: 0, no-undef: 0, camelcase: 0 */
/* global Stripe, listingJSON */

import React from 'react';
import PropTypes from 'prop-types';
import momentTz from 'moment-timezone';
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl, intlShape } from 'react-intl';
import { BookingStatus } from './booking-status';
import { getDisplayDateTime } from '../elements-forms/schedule';
import { Helpers } from 'shared-components/helpers/helpers';

// import SegmentUtil from '../../../lib/segment_util';
import { ListingImage } from '../elements-image/listing-image';
import { Avatar } from 'shared-components/image/avatar';

export const DisplayNameInfo = ({ name, organization }) => {
  return (
    <div data-testing-id="display-name-info">
      { name ?
        <div className="ellipsis name heavy">
          { name }
        </div> : null
      }
      { organization ?
        <div className="organization ellipsis text-gray-500">
          { organization }
        </div> : null
      }
    </div>
  );
};

export const Status = ({ status, price, currency, expires }) => {
  const { inboxHeading, mobileHeading } = status;
  const heading = inboxHeading || mobileHeading;

  return (
    <div>
      { status ?
        <div className={`text-${status.borderColor} status`}>
          {'string' === typeof(heading) ? heading.toLowerCase() : heading}
        </div> : ''
      }
      { price ?
        <div className="text-gray-dark">
          <FormattedNumber
            value={price}
            currency={currency}
            style="currency"
          />
        </div> : ''
      }
      { expires &&
        <div className="expire-pill hidden-xs">
          <FormattedMessage
            id='inbox.status.date_not_available'
            defaultMessage="expires in {expires}"
            values={{
              expires: expires.display_string
            }}
          />
        </div>
      }
    </div>
  );
};

export const NameStatusRow = ({ name, details, expires, viewAs }) => {
  return (
    <div className="visible-xs name-date-row">
      <ul className="list-inline">
        <li className="v-bottom">
          <DisplayNameInfo name={name} />
        </li>
        <li>
          <div className="text-small text-right">
            <BookingStatus details={details} >
              <Status
                expires={expires}
                viewAs={viewAs}
              />
            </BookingStatus>
          </div>
        </li>
      </ul>
    </div>
  );
};

class InboxPanel extends React.Component {
  constructor (props) {
    super(props);

    this.formatSchedule = this.formatSchedule.bind(this);
  }

  formatSchedule (time, flexible, objState, timeZoneId) {
    const isFlexible = flexible && 'INQUIRED' === objState
      ? this.props.intl.formatMessage({
        id: 'inbox.inbox_panel.flexible_label',
        defaultMessage: 'flexible'
      })
      : '';

    if (isFlexible && !time[0]) {
      return this.props.intl.formatMessage({ id: 'inbox.inbox_panel.dates_flexible', defaultMessage: 'Dates are flexible' });
    }


    if (!time[0]) {
      return this.props.intl.formatMessage({ id: 'inbox.inbox_panel.date_not_available', defaultMessage: 'Date & time not available' });
    }

    const { start, end, startDateTime, endDateTime } = time[0];
    const startTime = startDateTime ? startDateTime.format('h:mm a') : null;
    const endTime = endDateTime ? endDateTime.format('h:mm a') : null;
    const timeZone = startDateTime ? startDateTime.format('z') : null;
    const daysCount = time.length - 1;
    const isMultipleDays = 0 < daysCount
      ? 1 < daysCount
        ? this.props.intl.formatMessage({
          id: 'inbox.inbox_panel.more_days',
          defaultMessage: '+ {daysCount} more days'
        }, { daysCount })
        : this.props.intl.formatMessage({
          id: 'inbox.inbox_panel.more_day',
          defaultMessage: '+ {daysCount} more day'
        }, { daysCount })
      : null;
    const scheduleTextArray = [isMultipleDays, isFlexible];
    const scheduleTextString = scheduleTextArray.filter((type) => { return !!type; }).join(', ');

    const startDate = start.date &&
      <FormattedDate
        value={startDateTime}
        year="numeric"
        month="short"
        day="numeric"
        weekday="short"
        timeZone={timeZoneId}
      />;

    if ((start && start.time.h) && (end && (null !== end.time.h))) {
      const formattedStartTime = this.props.intl.formatTime(moment(startTime, 'LT').format());
      const formattedEndTime = this.props.intl.formatTime(moment(endTime, 'LT').format());
      return <span>{startDate}{startDateTime ? ', ' : ''}{formattedStartTime} - {formattedEndTime} {timeZone} {scheduleTextString ? `(${scheduleTextString})` : ''}</span>;
    }

    return (
      <div>
        { startDate }{startDate ? ',' : '' }&nbsp;
        {
          this.props.intl.formatMessage(
            {
              id: 'inbox.inbox_panel.not_specified',
              defaultMessage: 'Time not specified {flexible}'
            },
            {
              flexible: flexible
                ? this.props.intl.formatMessage({ id: 'inbox.inbox_panel.flexible', defaultMessage: '(flexible)' })
                : ''
            })
        }
      </div>
    );
  }

  render () {
    const {
      view_as,
      listing,
      host,
      guest,
      payment,
      payout,
      schedule,
      timezone,
      flexible_date_time,
      obj_state,
      is_system_managed,
      calculations = {}
    } = this.props.details;
    const {
      msgs
    } = this.props.thread;
    const avatarId = _.get(host || guest, 'avatar_id', '');
    const avatar = `https://img.peerspace.com/image/upload/c_crop,g_custom/f_auto,fl_progressive/c_scale,dpr_2.0,h_100,w_100/${avatarId}`;
    const image = {
      ratio: 1.689,
      widths: [200],
      defaultWidth: 400,
      defaultHeight: 236,
      alt: 'Peerspace',
      imgId: listing.cover_img_id,
      style: 'img-responsive listing-image',
      addTransform: 'e_brightness_hsb:-35/'
    };
    // list statuses under guest / host
    // that should get an expiration bubble
    const showExpiresByViewAsStatus = {
      HOST: {
        REQUESTED: true,
        GUEST_UPDATE_REQUESTED: true
      },
      GUEST: {
        OFFER_SUBMITTED: true,
        HOST_UPDATE_REQUESTED: true
      }
    };

    const zone = momentTz.tz.guess();
    const formatTime = msgs[0] ? momentTz.tz(msgs[0].created, zone).format('LT z') : '';
    const read = this.props.is_read ? this.props.is_read : false;
    const body = msgs[0] ? msgs[0].body : 'No messages';
    const { organization, name } = host || guest || {};
    const first = name ? name.first : '';
    const last = name ? name.last : '';
    const timeZoneId = timezone ? timezone.id : '';
    const displayDateTime = getDisplayDateTime({ timeZoneId, schedule });
    const formatSchedule = this.formatSchedule(displayDateTime, flexible_date_time, obj_state, timeZoneId);
    const expires_in = _.get(this.props, 'update_details.expires_in', null) || _.get(this.props, 'details.expires_in');
    const expires = (showExpiresByViewAsStatus[view_as] || {})[obj_state] ? expires_in : '';
    const isPowerHost = Helpers.isPowerHost(host);
    const guestPayment = is_system_managed ? _.get(calculations, 'guest.payment', '') : _.get(payment, 'total', {});
    const hostPayout = is_system_managed ? _.get(calculations, 'host.payout', '') : _.get(payout, 'total', {});
    const displayPrice = 'CANCELLED' === obj_state ? '' : 'HOST' === view_as ? hostPayout : guestPayment;
    const currency = _.get(payout, 'currency', '') || _.get(payment, 'currency', '');

    return (
      <div className={`row-height ${false === read ? 'unread' : ''}`} data-testing-id="inbox-panel">
        <div className="col-xl-2 col-lg-2 col-md-2 hidden-sm hidden-xs col-height col-middle no-gutter-left listing-img-col">
          <ListingImage
            {...image}
            containerStyle="img-cover"
          />
        </div>
        <div className="col-xl-2 col-lg-2 col-md-2 col-sm-3 col-xs-2 col-height col-middle no-gutter-right">
          <ul className="list-inline">
            <li className="col-middle">
              <Avatar src={avatar} addPowerHostBadge={ isPowerHost } />
            </li>
            <li className="col-middle hidden-xs">
              <DisplayNameInfo
                name={`${first} ${last}`}
                organization={'HOST' === view_as ? organization : ''}
              />
            </li>
          </ul>
        </div>
        <div className="col-xl-6 col-lg-5 col-md-4 col-sm-6 col-xs-9 col-height col-middle">
          <div className="indicator"></div>
          <NameStatusRow
            name={`${first} ${last}`}
            details={this.props.details}
            expires={expires}
            viewAs={view_as}
          />
          <div className="ellipsis subject">
            {('HOST' === view_as && listing.use_type) ? `${listing.use_type}: ` : ''}{formatSchedule}
          </div>
          <div className="text-gray-dark ellipsis text-small-xs">
            {body}
          </div>
          <div className="text-gray-500 text-small message-date">
            {msgs[0] && <FormattedDate
              value={msgs[0].created}
              year="numeric"
              month="short"
              day="numeric"
              weekday="short"
            />}
            &nbsp;
            { this.props.intl.formatTime(moment(formatTime, 'LT').format()) }
          </div>
        </div>
        <div className="col-md-2 col-sm-3 hidden-xs col-height col-middle text-right">
          <BookingStatus details={this.props.details} >
            <Status
              price={displayPrice}
              currency={currency}
              expires={expires}
              viewAs={view_as}
            />
          </BookingStatus>
        </div>
      </div>
    );
  }
}

InboxPanel.propTypes = {
  view_as: PropTypes.string,
  expires_in: PropTypes.object,
  listing: PropTypes.object,
  host: PropTypes.object,
  guest: PropTypes.object,
  payment: PropTypes.object,
  payout: PropTypes.object,
  schedule: PropTypes.string,
  thread: PropTypes.object,
  intl: intlShape
};

Status.propTypes = {
  status: PropTypes.object,
  name: PropTypes.string,
  price: PropTypes.number,
  currency: PropTypes.string,
  expires_in: PropTypes.object
};

exports.InboxPanel = injectIntl(InboxPanel);
